/**
 * Is a person on the page? Signals for telling real visitors from automated browsers in Clarity (2026-10-13).
 *
 * Clarity recorded "The sign-up form didn't load" sessions in the US and Canada that turned out to be
 * Microsoft-run automation (Azure IPs following gizmodata.com's sign-up button: 0 clicks, ~8 s, the cursor
 * gliding to a corner in a perfectly straight line). Clerk doesn't start in that sandbox, so the sessions
 * looked like lost sign-ups and couldn't be told apart from real ones.
 *
 * - `isAutomatedBrowser()`: the browser says it is driven by automation (`navigator.webdriver`, set by
 *   Playwright, Selenium, Puppeteer, headless Chrome). Cheap and spoofable, so it's a first filter only.
 * - `watchForHumanInput()`: reports the first input a script-driven browser rarely produces: a key press, a
 *   touch, a scroll wheel, or a mouse path that actually curves (bots tend to move in straight lines).
 *   Clarity's script isn't fetched until it fires (2026-10-16), so bots that hide `navigator.webdriver` aren't
 *   recorded either. gizmodata.com's index.html has the same check inline; change the two together.
 */
export type HumanInput = 'keys' | 'touch' | 'wheel' | 'curved-mouse';

export function isAutomatedBrowser(nav: Navigator | undefined = typeof navigator === 'undefined' ? undefined : navigator): boolean {
  try {
    return nav?.webdriver === true;
  } catch {
    return false;
  }
}

/** Direction changes (sin of the turn angle) a mouse path needs before it counts as a person's. */
const TURN_SIN = 0.12;
const TURNS_NEEDED = 3;
const MIN_STEP_PX = 4;

/** True when the path from a→b→c turns by more than ~7°. Segments shorter than MIN_STEP_PX are ignored. */
export function turns(a: { x: number; y: number }, b: { x: number; y: number }, c: { x: number; y: number }): boolean {
  const ux = b.x - a.x, uy = b.y - a.y, vx = c.x - b.x, vy = c.y - b.y;
  const lu = Math.hypot(ux, uy), lv = Math.hypot(vx, vy);
  if (lu < MIN_STEP_PX || lv < MIN_STEP_PX) return false;
  return Math.abs(ux * vy - uy * vx) / (lu * lv) > TURN_SIN;
}

let human: HumanInput | null = null;

/** The first human-looking input seen so far, or null. */
export const humanInput = (): HumanInput | null => human;

/** Calls `onFirst` once, on the first human-looking input (at once if one was already seen). Returns a function
 *  that stops watching. */
export function watchForHumanInput(onFirst: (kind: HumanInput) => void, target: Window = window): () => void {
  const points: { x: number; y: number }[] = [];
  let turnCount = 0;
  const seen = (kind: HumanInput) => {
    if (human) return;
    human = kind;
    stop();
    try { onFirst(kind); } catch { /* analytics must never break the page */ }
  };
  const onKey = (e: KeyboardEvent) => { if (e.isTrusted) seen('keys'); };
  const onTouch = (e: TouchEvent) => { if (e.isTrusted) seen('touch'); };
  const onWheel = (e: WheelEvent) => { if (e.isTrusted) seen('wheel'); };
  const onMove = (e: MouseEvent) => {
    if (!e.isTrusted) return;
    const p = { x: e.clientX, y: e.clientY };
    const last = points[points.length - 1];
    if (last && Math.hypot(p.x - last.x, p.y - last.y) < MIN_STEP_PX) return;
    points.push(p);
    if (points.length > 3) points.shift();
    if (points.length === 3 && turns(points[0], points[1], points[2]) && ++turnCount >= TURNS_NEEDED) seen('curved-mouse');
  };
  const opts = { passive: true, capture: true } as const;
  target.addEventListener('keydown', onKey, opts);
  target.addEventListener('touchstart', onTouch, opts);
  target.addEventListener('wheel', onWheel, opts);
  target.addEventListener('mousemove', onMove, opts);
  function stop() {
    target.removeEventListener('keydown', onKey, opts);
    target.removeEventListener('touchstart', onTouch, opts);
    target.removeEventListener('wheel', onWheel, opts);
    target.removeEventListener('mousemove', onMove, opts);
  }
  if (human) {             // Already seen by an earlier watcher: report it now.
    stop();
    try { onFirst(human); } catch { /* analytics must never break the page */ }
  }
  return stop;
}
